import React, { useState, useEffect } from "react";
import { fieldClass, selectClass } from "../fields/fieldStyles";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import { MdAdd, MdRemove } from "react-icons/md";
import ModalShell from "../modals/ModalShell";
import {
  Recipe,
  CreateRecipeInput,
  UpdateRecipeInput,
  ComponentAmount,
  RecipeComponentFormValue,
  RecipeSectionFormValue,
  IngredientEntity,
  MealType,
} from "../../types/recipe";
import { getMealTypeOptions } from "../../utils/recipeUtils";
import { formatNumber } from "../../utils/formatters";
import {
  buildComponentsDTO,
  hydrateComponentsFromRecipe,
  makeComponentKey,
  makeDefaultSection,
  makeSectionKey,
  SERVINGS_MIN,
  SERVINGS_MAX,
  NAME_MAX,
  DESCRIPTION_MAX,
  SECTION_NAME_MAX,
  NOTE_MAX,
  STEP_TEXT_MAX,
  PORTION_MIN_G,
  PORTION_MAX_G,
} from "../../utils/recipeMacros";
import { QUANTITY_MAX, QUANTITY_MIN } from "../../utils/measurementUnits";
import RecipeComponentsSection from "./RecipeComponentsSection";
import IngredientPickerModal from "./IngredientPickerModal";
import EmptyTabState from "./EmptyTabState";
import Button from "../common/Button";

// Error-key prefixes for section/portion-scoped validation messages.
const SECTION_ERROR_PREFIX = "section:";
const PORTION_ERROR_PREFIX = "portion:";

type RecipeModalTab = "components" | "directions";

const TABS: { id: RecipeModalTab; label: string; shortLabel: string }[] = [
  { id: "components", label: "Ingredients", shortLabel: "Ingredients" },
  { id: "directions", label: "Cooking Directions", shortLabel: "Directions" },
];

interface RecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRecipeInput | UpdateRecipeInput) => void;
  recipe?: Recipe | null;
  mode: "create" | "edit";
  loading: boolean;
  error?: string | null;
}

const RecipeModal: React.FC<RecipeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  recipe,
  mode,
  loading,
  error: responseError,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    default_num_of_servings: 1,
    description: "",
    directionSteps: [] as string[],
    meal_type: MealType.MEAL as MealType,
    note: "",
  });

  const [components, setComponents] = useState<RecipeComponentFormValue[]>([]);
  const [sections, setSections] = useState<RecipeSectionFormValue[]>([]);
  const [activeTab, setActiveTab] = useState<RecipeModalTab>("components");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetSectionId, setPickerTargetSectionId] = useState<
    string | null
  >(null);

  const parseDirectionsToSteps = (directions?: string | null): string[] => {
    if (!directions || !directions.trim()) return [];
    const steps = directions.split(/STEP\s*\d+\.\s*/i).filter((s) => s.trim());
    return steps.length > 0 ? steps.map((s) => s.trim()) : [directions.trim()];
  };

  const stepsToDirections = (steps?: string[] | null): string => {
    if (!steps || steps.length === 0) return "";
    const nonEmpty = steps.filter((s) => s && s.trim());
    if (nonEmpty.length === 0) return "";
    return nonEmpty.map((step, i) => `STEP ${i + 1}. ${step.trim()}`).join(" ");
  };

  // Not on useModalForm: formData is one of several state slots (components,
  // sections, tab, picker) that reset together in the effect below.
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && recipe) {
        setFormData({
          name: recipe.name,
          default_num_of_servings: recipe.default_num_of_servings || 1,
          description: recipe.description || "",
          directionSteps: parseDirectionsToSteps(recipe.directions),
          meal_type: recipe.meal_type || MealType.MEAL,
          note: recipe.note || "",
        });
        const hydrated = hydrateComponentsFromRecipe(recipe);
        setComponents(hydrated.components);
        setSections(hydrated.sections);
      } else {
        setFormData({
          name: "",
          default_num_of_servings: 1,
          description: "",
          directionSteps: [],
          meal_type: MealType.MEAL,
          note: "",
        });
        setComponents([]);
        // A recipe always starts with a "Main" section, as in the app.
        setSections([makeDefaultSection()]);
      }
      setErrors({});
      setActiveTab("components");
      setPickerOpen(false);
      setPickerTargetSectionId(null);
    }
  }, [isOpen, mode, recipe]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Recipe name is required";
    } else if (formData.name.trim().length > NAME_MAX) {
      newErrors.name = `Name cannot exceed ${NAME_MAX} characters`;
    }

    if (formData.description.trim().length > DESCRIPTION_MAX) {
      newErrors.description = `Description cannot exceed ${formatNumber(
        DESCRIPTION_MAX
      )} characters`;
    }

    const nameCounts = new Map<string, number>();
    sections.forEach((section) => {
      const trimmed = section.name.trim().toLowerCase();
      if (trimmed) nameCounts.set(trimmed, (nameCounts.get(trimmed) || 0) + 1);
    });
    sections.forEach((section) => {
      const trimmed = section.name.trim();
      if (!trimmed) {
        newErrors[`${SECTION_ERROR_PREFIX}${section.id}`] =
          "Section name is required";
      } else if ((nameCounts.get(trimmed.toLowerCase()) || 0) > 1) {
        newErrors[`${SECTION_ERROR_PREFIX}${section.id}`] =
          "Section names must be unique";
      } else if (trimmed.length > SECTION_NAME_MAX) {
        newErrors[
          `${SECTION_ERROR_PREFIX}${section.id}`
        ] = `Section name cannot exceed ${SECTION_NAME_MAX} characters`;
      }
    });

    components.forEach((component) => {
      const errorKey = `${PORTION_ERROR_PREFIX}${component.key}`;
      if (component.measurement_unit_label !== null) {
        const quantity = component.quantity;
        if (
          quantity === null ||
          !Number.isFinite(quantity) ||
          quantity < QUANTITY_MIN ||
          quantity > QUANTITY_MAX ||
          Math.round(quantity * 100) / 100 !== quantity
        ) {
          newErrors[
            errorKey
          ] = `Quantity must be between ${QUANTITY_MIN} and ${formatNumber(
            QUANTITY_MAX
          )}`;
          return;
        }
      }
      // Derived grams are held to the same limits as typed grams.
      if (
        component.portion_grams < PORTION_MIN_G ||
        component.portion_grams > PORTION_MAX_G
      ) {
        newErrors[
          errorKey
        ] = `Portion must be between ${PORTION_MIN_G} and ${formatNumber(
          PORTION_MAX_G
        )} g`;
      }
    });

    if (components.length === 0) {
      newErrors.components = "Add at least one ingredient.";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      const keys = Object.keys(newErrors);
      const hasComponentErrors = keys.some(
        (k) =>
          k.startsWith(SECTION_ERROR_PREFIX) ||
          k.startsWith(PORTION_ERROR_PREFIX) ||
          k === "components"
      );
      if (hasComponentErrors) {
        setActiveTab("components");
      }
      return;
    }

    const componentsDTO = buildComponentsDTO(components, sections);

    const submitData = {
      name: formData.name.trim(),
      default_num_of_servings: formData.default_num_of_servings,
      description: formData.description.trim() || null,
      components: componentsDTO,
      directions: stepsToDirections(formData.directionSteps) || null,
      meal_type: formData.meal_type,
      note: formData.note.trim() || null,
    };

    onSubmit(submitData);
  };

  // Component/section handlers
  const handleAddComponentToSection = (sectionId: string) => {
    setPickerTargetSectionId(sectionId);
    setPickerOpen(true);
  };

  const handlePickIngredient = (
    ingredient: IngredientEntity,
    amount: ComponentAmount
  ) => {
    const targetSectionId = pickerTargetSectionId ?? sections[0]?.id ?? null;
    setComponents((prev) => [
      ...prev,
      {
        key: makeComponentKey(),
        food: ingredient,
        ...amount,
        sectionId: targetSectionId,
      },
    ]);
    setPickerOpen(false);
    setPickerTargetSectionId(null);
  };

  const handleUpdateAmount = (key: string, amount: ComponentAmount) => {
    setComponents((prev) =>
      prev.map((c) => (c.key === key ? { ...c, ...amount } : c))
    );
  };

  const handleRemoveComponent = (key: string) => {
    setComponents((prev) => prev.filter((c) => c.key !== key));
  };

  const handleAddSection = () => {
    setSections((prev) => [...prev, { id: makeSectionKey(), name: "" }]);
  };

  const handleRenameSection = (id: string, name: string) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
  };

  // The last section cannot be removed; ingredients of a removed section
  // move to the first remaining one.
  const handleRemoveSection = (id: string) => {
    if (sections.length <= 1) return;
    const remaining = sections.filter((s) => s.id !== id);
    const fallbackId = remaining[0].id;
    setComponents((prev) =>
      prev.map((c) =>
        c.sectionId === id ? { ...c, sectionId: fallbackId } : c
      )
    );
    setSections(remaining);
  };

  const componentErrors = {
    components: errors.components,
    sections: Object.fromEntries(
      Object.entries(errors)
        .filter(([k]) => k.startsWith(SECTION_ERROR_PREFIX))
        .map(([k, v]) => [k.slice(SECTION_ERROR_PREFIX.length), v])
    ),
    portions: Object.fromEntries(
      Object.entries(errors)
        .filter(([k]) => k.startsWith(PORTION_ERROR_PREFIX))
        .map(([k, v]) => [k.slice(PORTION_ERROR_PREFIX.length), v])
    ),
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onClose={onClose}
        title={mode === "create" ? "Create New Recipe" : "Edit Recipe"}
        size="4xl"
        layout="column"
        closeDisabled={loading}
        message={responseError ? { text: responseError, type: "error" } : null}
        onSubmit={handleSubmit}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              loadingLabel={mode === "create" ? "Creating..." : "Updating..."}
            >
              {mode === "create" ? "Create Recipe" : "Update Recipe"}
            </Button>
          </>
        }
      >
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <Label>Recipe Name *</Label>
                  <input
                    type="text"
                    value={formData.name}
                    maxLength={NAME_MAX}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className={fieldClass()}
                    placeholder="Enter recipe name"
                  />
                  <FieldError>{errors.name}</FieldError>
                </div>

                <div>
                  <Label>Meal Type *</Label>
                  <select
                    value={formData.meal_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        meal_type: e.target.value as MealType,
                      }))
                    }
                    className={selectClass()}
                    required
                  >
                    {getMealTypeOptions().map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <Label>Servings</Label>
                  <input
                    type="number"
                    min={SERVINGS_MIN}
                    max={SERVINGS_MAX}
                    step="1"
                    value={formData.default_num_of_servings}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value);
                      const clamped = Number.isNaN(raw)
                        ? SERVINGS_MIN
                        : Math.min(SERVINGS_MAX, Math.max(SERVINGS_MIN, raw));
                      setFormData((prev) => ({
                        ...prev,
                        default_num_of_servings: clamped,
                      }));
                    }}
                    className={fieldClass()}
                    placeholder="1"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label>Description</Label>
                <textarea
                  rows={3}
                  maxLength={DESCRIPTION_MAX}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className={fieldClass()}
                  placeholder="Describe your recipe..."
                />
                <FieldError>{errors.description}</FieldError>
              </div>

              {/* Note */}
              <div>
                <Label>Note</Label>
                <textarea
                  rows={2}
                  maxLength={NOTE_MAX}
                  value={formData.note}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                  }
                  className={fieldClass()}
                  placeholder="Additional notes..."
                />
              </div>

              {/* Content tabs */}
              <div className="flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`min-w-0 flex-1 whitespace-nowrap px-1 py-2 text-xs font-medium sm:px-4 sm:text-sm ${
                      activeTab === tab.id
                        ? "bg-biafit-primary text-white"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/60"
                    }`}
                  >
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.shortLabel}</span>
                  </button>
                ))}
              </div>

              {/* Ingredients */}
              <div className={activeTab === "components" ? "" : "hidden"}>
                <RecipeComponentsSection
                  components={components}
                  sections={sections}
                  servings={formData.default_num_of_servings}
                  disabled={loading}
                  onAddComponentToSection={handleAddComponentToSection}
                  onUpdateAmount={handleUpdateAmount}
                  onRemoveComponent={handleRemoveComponent}
                  onAddSection={handleAddSection}
                  onRenameSection={handleRenameSection}
                  onRemoveSection={handleRemoveSection}
                  onReorderComponents={setComponents}
                  onReorderSections={setSections}
                  errors={componentErrors}
                />
              </div>

              <div className={activeTab === "directions" ? "" : "hidden"}>
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Cooking Directions
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        directionSteps: [...prev.directionSteps, ""],
                      }))
                    }
                    className="inline-flex items-center rounded-md bg-biafit-primary px-3 py-1 text-sm font-medium text-white hover:bg-opacity-90"
                  >
                    <MdAdd className="mr-1 h-4 w-4" />
                    Add Step
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.directionSteps.length === 0 && (
                    <EmptyTabState
                      message="No steps yet — add a step to get started."
                      buttonLabel="Add Step"
                      onAdd={() =>
                        setFormData((prev) => ({
                          ...prev,
                          directionSteps: [...prev.directionSteps, ""],
                        }))
                      }
                    />
                  )}
                  {formData.directionSteps.map((step, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-biafit-primary text-sm font-bold text-white">
                        {index + 1}
                      </span>
                      <textarea
                        rows={2}
                        maxLength={STEP_TEXT_MAX}
                        value={step}
                        onChange={(e) => {
                          const newSteps = [...formData.directionSteps];
                          newSteps[index] = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            directionSteps: newSteps,
                          }));
                        }}
                        className={fieldClass(false, "flex-1")}
                        placeholder={`Step ${index + 1}...`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newSteps = formData.directionSteps.filter(
                            (_, i) => i !== index
                          );
                          setFormData((prev) => ({
                            ...prev,
                            directionSteps: newSteps,
                          }));
                        }}
                        className="mt-2 text-red-600 hover:text-red-800"
                      >
                        <MdRemove className="h-5 w-5" />
                      </button>
                    </div>
                  ))}

                  {formData.directionSteps.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          directionSteps: [...prev.directionSteps, ""],
                        }))
                      }
                      className="flex w-full items-center justify-center rounded-md border border-dashed border-gray-300 py-2 text-sm font-medium text-biafit-primary transition-colors duration-200 hover:border-biafit-primary hover:bg-biafit-primary/5 dark:border-gray-600 dark:hover:border-biafit-primary"
                    >
                      <MdAdd className="mr-1 h-4 w-4" />
                      Add Step
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalShell>

      <IngredientPickerModal
        isOpen={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setPickerTargetSectionId(null);
        }}
        onPick={handlePickIngredient}
      />
    </>
  );
};

export default RecipeModal;
